import { OAUTH_CALLBACK_PATH, oidc } from '../config';
import {
  generateCodeChallenge,
  generateCodeVerifier,
  generateState,
  storeOAuthState,
  storePkceVerifier,
  storeRedirectPath,
} from './pkce';
import { clearTokens, revokeRefreshToken } from './tokens';

export function redirectUri(): string {
  return new URL(OAUTH_CALLBACK_PATH, window.location.origin).href;
}

/** Start the authorization-code + PKCE flow by redirecting to the hosted UI. */
export async function initiateLogin(redirectPath?: string): Promise<void> {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateState();

  storePkceVerifier(codeVerifier);
  storeOAuthState(state);
  storeRedirectPath(
    redirectPath ?? window.location.pathname + window.location.search
  );

  const url = new URL(oidc.authorizeEndpoint);
  url.searchParams.set('client_id', oidc.clientId);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', oidc.scopes);
  url.searchParams.set('redirect_uri', redirectUri());
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', codeChallenge);
  url.searchParams.set('code_challenge_method', 'S256');
  window.location.href = url.href;
}

/**
 * Log out: revoke the refresh token, drop local tokens, then visit the IdP's
 * end-session endpoint so the Cognito session cookie is cleared too. Without
 * that last step the next visit would silently log the user straight back in.
 */
export async function initiateLogout(): Promise<void> {
  await revokeRefreshToken();
  clearTokens();
  if (oidc.endSessionEndpoint) {
    const url = new URL(oidc.endSessionEndpoint);
    url.searchParams.set('client_id', oidc.clientId);
    // Must be one of the app client's registered sign-out URLs (`<origin>/`).
    url.searchParams.set('logout_uri', `${window.location.origin}/`);
    window.location.href = url.href;
  } else {
    window.location.href = '/';
  }
}
